import { useCallback, useState, useRef, useMemo, useEffect } from "react";
import { useLocation, useParams } from "wouter";
import { Button } from "@/components/ui/button";
import { Upload, Download, Loader2, AlertCircle, Share2, Camera, Eye, Copy, Printer } from "lucide-react";
import { useResultGate } from "@/lib/resultGate";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";
import { getToolById } from "@/lib/tools-data";
import { trpc } from "@/lib/trpc";
import { getOrCreateDeviceId } from "@/lib/deviceId";
import { showDailyLimitPaywall } from "@/hooks/usePaywallDialog";
import { ExportUsageNote } from "@/components/ExportUsageNote";
import { DownloadTrialHint } from "@/components/DownloadTrialHint";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { guardedGate, withDownloadGate } from "@/lib/downloadGate";
import { usePipelineContext } from "@/lib/pipelineContext";
import { clearHandoff, continueWith, handoffFiles, setHandoff, useHandoffFor } from "@/lib/handoff";
import { isToolSessionCounted, markToolSessionCounted, useUsageSnapshot } from "@/lib/usageGate";
import { markBlobForTrial, markKindFor, wouldMark, zipMayBeMarked } from "@/lib/trialMark";
import { unlockPdfIfNeeded, markSkippedLocked } from "@/tools/pdf/unlockPdf";
import { isUnreadablePdf } from "@/tools/pdf/damagedPdf";
import { requestPdfPassword } from "@/hooks/usePdfPasswordPrompt";
import { partitionByAccept, describeAccept } from "@/lib/fileAccept";
import { CloudPickerButton } from "@/components/CloudPickerButton";
import { StickyActionBar, STICKY_BAR_PRIMARY_CLASS } from "@/components/StickyActionBar";

interface FileDropZoneProps {
  accept?: string;
  multiple?: boolean;
  maxSize?: number; // in MB
  onFiles: (files: File[]) => void;
  label?: string;
  description?: string;
  /** Show "Take photo" (opens mobile camera). Defaults true for image accepts. */
  enableCamera?: boolean;
  /** For multi-file batch tools (Merge PDF and similar) where the real
   * "value moment" is a separate processing action, not each file add --
   * skips this component's own per-add usage log. Pair with
   * useLogUsageOnAction's logUsageNow(), called explicitly at that real
   * action instead. See that hook's own doc comment for the full
   * explanation of the bug this fixes (tidying a multi-file list -- an
   * accidental duplicate add/remove -- was burning extra free uses for
   * what was still just one unfinished job). */
  deferUsageLog?: boolean;
  /** Load the file even when today's trial uses are gone, and take no use now: the use is counted (and the
   * paywall shown) at Download, when a result exists. Implies deferUsageLog. Set on tools whose result goes
   * out through DownloadButton or an explicit usage check of its own. */
  gateAtDownload?: boolean;
  /** Let a PDF that nothing can read through instead of turning it away at
   * selection -- only for a tool whose whole purpose is handling damaged
   * PDFs (Repair PDF). */
  acceptDamagedPdf?: boolean;
  /** What to call the accepted file types in the "can't be used" message, when the accept list is long or broader than what the tool converts. */
  acceptLabel?: string;
  /** Called as soon as files are dropped or picked, before any check, so a tool can clear a message left by the previous attempt. */
  onAttempt?: () => void;
  /** Called when a PDF is turned away as unreadable, so the tool can also show the reason on its own page (the toast is brief). */
  onUnreadablePdf?: (file: File) => void;
  /** When someone skips a password-protected PDF, still hand it to the tool (marked as skipped)
   * instead of dropping it, so the tool can show it with an "Unlock" button. Off by default: most
   * tools can't do anything with a locked file. */
  keepSkippedProtectedFiles?: boolean;
  /** On phones, once files are in the list, shrink the big drop box to a 48px "+ Add more files" button and hide the
   * camera button, so the action under it stays near the top. Desktop is unchanged. */
  compactOnPhone?: boolean;
}

/** "Drop a PDF to compress, or click to browse" reads wrong on a phone, where nothing can be dropped: "Tap to choose a PDF to compress". */
function touchLabel(label: string): string {
  const m = label.match(/^Drop (.+?)(?:,? (?:here )?or click to browse| here)?$/i);
  return m ? `Tap to choose ${m[1].replace(/\s+here$/i, "")}` : label;
}

export function FileDropZone({
  accept = "*",
  multiple = false,
  maxSize = 50,
  onFiles,
  label = "Drop files here or click to browse",
  description,
  enableCamera,
  deferUsageLog: deferUsageLogProp = false,
  gateAtDownload = false,
  acceptDamagedPdf = false,
  onUnreadablePdf,
  acceptLabel,
  onAttempt,
  keepSkippedProtectedFiles = false,
  compactOnPhone = false,
}: FileDropZoneProps) {
  const deferUsageLog = deferUsageLogProp || gateAtDownload;
  const [isDragging, setIsDragging] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [checkingCount, setCheckingCount] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const { isAuthenticated } = useAuth();
  const params = useParams<{ id?: string }>();
  const utils = trpc.useUtils();
  const logUsage = trpc.usage.log.useMutation();
  const pipeline = usePipelineContext();
  // In a bundle run, the first step remembers the person's file name so the final file can be named after it.
  const rememberStartName = pipeline && pipeline.currentStepIndex === 0 ? pipeline.setOriginalName : undefined;

  const toolId = params.id ?? "unknown";
  const category = getToolById(toolId)?.category ?? "unknown";
  const carried = useHandoffFor(toolId); // a result brought here with "Continue with..."

  const showCamera =
    enableCamera ??
    (typeof accept === "string" &&
      (accept.includes("image") ||
        accept.includes("png") ||
        accept.includes("jpg") ||
        accept.includes("jpeg") ||
        accept.includes("webp") ||
        accept.includes("heic") ||
        accept.includes("gif")));

  /**
   * Gate for file-based tools. Free (non-file) tools never call this.
   * - Anonymous visitors get a real check against their own free daily
   *   uses, tracked by an anonymous device ID -- no login required for
   *   the free tier at all.
   * - Once the free daily limit is genuinely exhausted (logged in or
   *   not), this is what actually blocks the upload and shows the
   *   upgrade prompt.
   * - Fails OPEN on any network/server error so a transient glitch
   *   never blocks a legitimate user.
   */
  const checkAccess = useCallback(async (): Promise<boolean> => {
    try {
      const deviceId = isAuthenticated ? undefined : getOrCreateDeviceId();
      const result = await utils.client.usage.check.query({ toolId, deviceId });
      if (!result.allowed) {
        showDailyLimitPaywall(toolId);
        return false;
      }
      return true;
    } catch (error) {
      // Fail open: never block a legitimate user over our own error.
      console.error("[usage.check] failed, allowing by default:", error);
      return true;
    }
  }, [isAuthenticated, toolId, utils]);

  const handleFiles = useCallback(
    async (fileList: FileList | File[]) => {
      onAttempt?.();
      let files = Array.from(fileList);
      if (rememberStartName && files[0]) rememberStartName(files[0].name);

      // A dropped file skips the picker's `accept` filter, so check again here. Turned away before
      // anything else happens: no password prompt, no paywall, and no free use spent on it.
      const { ok: acceptable, rejected } = partitionByAccept(files, accept);
      if (rejected.length > 0) {
        const kind = acceptLabel ?? describeAccept(accept);
        const single = rejected.length === 1;
        toast.error(single ? `"${rejected[0].name}" can't be used here` : `${rejected.length} files can't be used here`, {
          description:
            (kind === "supported" ? "Check the file type this tool accepts." : `This tool takes ${kind} files.`) +
            (single ? "" : ` Skipped: ${rejected.slice(0, 3).map((r) => r.name).join(", ")}${rejected.length > 3 ? ` and ${rejected.length - 3} more` : ""}.`),
        });
      }
      files = acceptable;
      if (files.length === 0) return;

      // A one-file tool uses the first file and says so, instead of quietly asking about passwords
      // and damage for files it will never use.
      if (!multiple && files.length > 1) {
        toast.info(`This tool works on one file at a time — using "${files[0].name}"`, {
          description: `${files.length - 1} other file${files.length - 1 === 1 ? " was" : "s were"} left out.`,
        });
        files = files.slice(0, 1);
      }

      // No hard cap -- large files are allowed, just flagged so the
      // person knows what to expect before it happens. maxSize (default
      // 50) is now a warning threshold, not a block; tools that still
      // pass an explicit maxSize for a genuine technical reason (e.g. a
      // provider API with a real upload cap) should switch to a
      // dedicated hard-limit check of their own rather than relying on
      // this warning-only path.
      const oversized = files.filter(f => f.size > maxSize * 1024 * 1024);
      if (oversized.length > 0) {
        const mb = Math.round(oversized[0].size / (1024 * 1024));
        toast.warning(`This file is ${mb}MB, larger than ${maxSize}MB`, {
          description: "Larger files can slow down your browser during processing. It'll still work — just give it a moment, and you can stop anytime if it's taking too long.",
          duration: 6000,
        });
      }

      setIsChecking(true);
      setCheckingCount(files.length);
      try {
        // Once this visit to the tool has counted a use, later files in the same visit are part of the same job.
        const sessionCounted = isToolSessionCounted(toolId);
        // A tool that defers its usage log counts the use at its own action (and shows the paywall there), so the files
        // being added are kept at 0 trial uses instead of opening the paywall and dropping them (B-0156, Audio Merger).
        const allowed = sessionCounted || deferUsageLog ? true : await checkAccess();
        if (!allowed) return;

        // Password-protected PDFs: ask once, right here, and hand the
        // tool a decrypted copy -- so every PDF tool works on them and
        // the result is an ordinary unprotected file. Done after the
        // usage check (no point asking for a password if the limit
        // blocks the upload anyway) and before the usage log (a file
        // the person skips shouldn't burn a use).
        const ready: File[] = [];
        for (const f of files) {
          const isPdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name);
          if (!isPdf) { ready.push(f); continue; }
          const result = await unlockPdfIfNeeded(f, (name, wrong) => requestPdfPassword(name, wrong, !multiple));
          if (result.status === "cancelled") {
            if (keepSkippedProtectedFiles) {
              markSkippedLocked(f);
              ready.push(f);
              toast.info(`"${f.name}" is password-protected — it's in your list, locked. Use Unlock on its row when you have the password.`);
            } else {
              toast.info(`Skipped "${f.name}" — it's password-protected`);
            }
          } else if (result.status === "failed") {
            toast.error(`Couldn't open "${f.name}" — its protection isn't supported here`);
          } else {
            if (result.status === "unlocked") toast.success(`Unlocked "${f.name}"`);
            // A file nothing can read is named and turned away here, with a
            // way forward -- rather than each tool failing later with its
            // own generic message that doesn't say which file it was.
            if (result.status === "unchanged" && !acceptDamagedPdf && (await isUnreadablePdf(result.file))) {
              onUnreadablePdf?.(f);
              toast.error(`"${f.name}" can't be read — it looks damaged`, {
                description: "Try the Repair PDF tool, then add the repaired file.",
                duration: 10000,
                action: { label: "Repair PDF", onClick: () => { window.location.assign("/tool/repair-pdf"); } },
              });
            } else {
              ready.push(result.file);
            }
          }
        }
        if (ready.length === 0) return;

        // Real usage log for every use (not just authenticated users --
        // this is also what the daily count itself is based on).
        // Failures are ignored -- selecting a file must never break
        // because of telemetry. Skipped when deferUsageLog is set --
        // see that prop's own doc comment; the caller logs explicitly
        // at its own real action moment instead via
        // useLogUsageOnAction's logUsageNow().
        if (!deferUsageLog && !sessionCounted) {
          try {
            const deviceId = isAuthenticated ? undefined : getOrCreateDeviceId();
            await logUsage.mutateAsync({ toolId, category, deviceId });
            markToolSessionCounted(toolId);
            utils.usage.check.invalidate();
          } catch {
            /* ignore */
          }
        }
        onFiles(ready);
      } finally {
        setIsChecking(false);
        setCheckingCount(0);
      }
    },
    [accept, acceptLabel, onAttempt, multiple, maxSize, onFiles, isAuthenticated, checkAccess, logUsage, toolId, category, utils.usage.check, deferUsageLog, gateAtDownload, acceptDamagedPdf, onUnreadablePdf, keepSkippedProtectedFiles, rememberStartName]
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles]
  );

  return (
    <div className="space-y-3">
      {!pipeline && carried && (
        <div className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border border-primary/30 bg-primary/5" data-testid="carried-file">
          <button
            type="button"
            onClick={() => { const all = handoffFiles(carried); clearHandoff(); void handleFiles(multiple ? all : all.slice(0, 1)); }}
            className="flex-1 text-left"
          >
            <span className="text-sm">
              <span className="font-medium">Use {carried.more?.length ? `the ${carried.more.length + 1} results` : "the result"} from {getToolById(carried.fromToolId)?.name ?? "the last tool"}</span>
              <span className="text-muted-foreground">: {carried.more?.length ? `${carried.fileName} and ${carried.more.length} more` : carried.fileName}</span>
            </span>
          </button>
          <button type="button" onClick={clearHandoff} className="text-xs text-muted-foreground hover:text-foreground shrink-0">Dismiss</button>
        </div>
      )}
      {pipeline && pipeline.handoffFile && (
        <button
          type="button"
          onClick={() => handleFiles([new File([pipeline.handoffFile!.blob], pipeline.handoffFile!.fileName, { type: pipeline.handoffFile!.blob.type })])}
          className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border border-primary/30 bg-primary/5 text-left hover:bg-primary/10 transition-colors"
        >
          <span className="text-sm">
            <span className="font-medium">Use the latest result</span>
            <span className="text-muted-foreground">: {pipeline.handoffFile.fileName}</span>
          </span>
          <span className="text-xs text-primary font-medium shrink-0">Use this file</span>
        </button>
      )}
      {compactOnPhone && (
        <button
          type="button"
          data-testid="add-more-files"
          onClick={() => inputRef.current?.click()}
          className="min-h-12 w-full rounded-xl border-2 border-dashed border-primary/60 px-4 font-semibold text-primary sm:hidden"
        >
          + Add more files
        </button>
      )}
      <div
        className={`rounded-xl p-8 text-center cursor-pointer transition-all duration-200 ${
          compactOnPhone ? "max-sm:hidden " : ""
        }${isDragging ? "scale-[1.01]" : ""}`}
        style={{
          border: `2px dashed color-mix(in srgb, var(--signal) ${isDragging ? "90%" : "55%"}, transparent)`,
          backgroundColor: isDragging ? "color-mix(in srgb, var(--signal) 6%, transparent)" : "transparent",
          boxShadow: isDragging
            ? "0 0 0 4px color-mix(in srgb, var(--signal) 18%, transparent), 0 8px 28px color-mix(in srgb, var(--signal) 25%, transparent), 0 2px 8px rgba(0,0,0,0.12)"
            : "0 0 0 1px color-mix(in srgb, var(--signal) 8%, transparent), 0 4px 16px color-mix(in srgb, var(--signal) 10%, transparent), 0 1px 3px rgba(0,0,0,0.08)",
        }}
        onMouseEnter={e => {
          if (isDragging) return;
          e.currentTarget.style.border = `2px dashed color-mix(in srgb, var(--signal) 80%, transparent)`;
          e.currentTarget.style.boxShadow =
            "0 0 0 3px color-mix(in srgb, var(--signal) 14%, transparent), 0 8px 24px color-mix(in srgb, var(--signal) 20%, transparent), 0 2px 6px rgba(0,0,0,0.1)";
        }}
        onMouseLeave={e => {
          if (isDragging) return;
          e.currentTarget.style.border = `2px dashed color-mix(in srgb, var(--signal) 55%, transparent)`;
          e.currentTarget.style.boxShadow =
            "0 0 0 1px color-mix(in srgb, var(--signal) 8%, transparent), 0 4px 16px color-mix(in srgb, var(--signal) 10%, transparent), 0 1px 3px rgba(0,0,0,0.08)";
        }}
        onMouseDown={e => {
          e.currentTarget.style.border = `3px dashed color-mix(in srgb, var(--signal) 95%, transparent)`;
          e.currentTarget.style.boxShadow =
            "0 0 0 5px color-mix(in srgb, var(--signal) 20%, transparent), 0 10px 32px color-mix(in srgb, var(--signal) 30%, transparent), 0 3px 10px rgba(0,0,0,0.14)";
        }}
        onMouseUp={e => {
          e.currentTarget.style.border = `2px dashed color-mix(in srgb, var(--signal) 80%, transparent)`;
          e.currentTarget.style.boxShadow =
            "0 0 0 3px color-mix(in srgb, var(--signal) 14%, transparent), 0 8px 24px color-mix(in srgb, var(--signal) 20%, transparent), 0 2px 6px rgba(0,0,0,0.1)";
        }}
        onDragOver={e => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        // A keyboard and screen-reader user reaches the drop zone as a button named by its own text (B-0155).
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          onChange={e => {
            if (e.target.files && e.target.files.length > 0) {
              handleFiles(e.target.files);
              e.target.value = "";
            }
          }}
        />
        {isChecking ? (
          <Loader2 className="h-8 w-8 mx-auto mb-3 text-muted-foreground animate-spin" />
        ) : (
          <Upload className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
        )}
        <p className="font-medium text-sm">
          {isChecking && checkingCount > 0 ? `Preparing ${checkingCount} ${checkingCount === 1 ? "file" : "files"}…` : (
            <>
              <span className="[@media(pointer:coarse)]:hidden">{label}</span>
              <span className="hidden [@media(pointer:coarse)]:inline">{touchLabel(label)}</span>
            </>
          )}
        </p>
        {!/browse|choose|click|tap/i.test(label) && (
          <p className="text-xs text-muted-foreground mt-0.5">
            <span className="[@media(pointer:coarse)]:hidden">or click to browse</span>
            <span className="hidden [@media(pointer:coarse)]:inline">or tap to choose</span>
          </p>
        )}
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
        <p className="text-xs text-muted-foreground mt-2">Large files (over {maxSize >= 1000 ? `${maxSize / 1000} GB` : `${maxSize} MB`}) may take longer or run out of browser memory</p>
      </div>

      <CloudPickerButton provider="google" mode="open" className="w-full" accept={accept} multiple={multiple} onFiles={(files) => handleFiles(files)} />

      {showCamera && (
        <div className={compactOnPhone ? "max-sm:hidden" : undefined}>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={e => {
              if (e.target.files && e.target.files.length > 0) {
                handleFiles(e.target.files);
                e.target.value = "";
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            className="w-full gap-2"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              cameraInputRef.current?.click();
            }}
          >
            <Camera className="h-4 w-4" />
            Take photo with camera
          </Button>
        </div>
      )}
    </div>
  );
}

interface ToolContainerProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * The text of a calculator/converter result, for Copy and Save. Uses the
 * elements marked data-result when the tool has them; otherwise whatever
 * is readable in the container once the person's own controls, the info
 * panels and anything marked data-no-copy are taken out.
 */
function collectResultText(root: HTMLElement): string {
  const marked = Array.from(root.querySelectorAll<HTMLElement>("[data-result]"));
  const sources = marked.length ? marked : [root];
  const parts: string[] = [];
  for (const src of sources) {
    const clone = src.cloneNode(true) as HTMLElement;
    clone.querySelectorAll("input, textarea, select, button, label, svg, script, style, [data-no-copy], [role='tablist'], nav").forEach((n) => n.remove());
    // innerText on a detached node ignores layout, so read lines from block elements instead.
    const holder = document.createElement("div");
    holder.style.cssText = "position:fixed;left:-9999px;top:0;white-space:pre-wrap";
    holder.appendChild(clone);
    document.body.appendChild(holder);
    const text = (clone.innerText || clone.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
    document.body.removeChild(holder);
    if (text) parts.push(text);
  }
  return parts.join("\n\n");
}

function ResultActionsBar({ rootRef }: { rootRef: React.RefObject<HTMLDivElement | null> }) {
  const rg = useResultGate();
  const params = useParams<{ id?: string }>();
  if (!rg.active || rg.ownActions || !rg.hasResult) return null;
  const textOf = () => (rootRef.current ? collectResultText(rootRef.current) : "");
  const signature = "\n\nCalculated with FreeDailyPro.com";

  async function copy() {
    if (!rg.gate || !(await rg.gate("copy"))) return;
    const t = textOf();
    if (!t) return;
    try {
      // The gate has already counted this visit's use, so the guarded clipboard call passes at no cost.
      await navigator.clipboard.writeText(t + signature);
      toast.success("Result copied");
    } catch {
      toast.error("Couldn't copy. Select the result and press Ctrl+C instead.");
    }
  }
  async function save() {
    if (!rg.gate || !(await rg.gate())) return;
    const t = textOf();
    if (!t) return;
    triggerDownload(new Blob([t + signature + "\n"], { type: "text/plain;charset=utf-8" }), `${params.id ?? "result"}-result.txt`);
  }
  async function print() {
    if (!rg.gate || !(await rg.gate("print"))) return;
    window.print();
  }
  return (
    <div className="no-print mt-6 flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2" data-testid="result-actions">
      <Button type="button" size="sm" variant="outline" onClick={copy}><Copy className="h-4 w-4 mr-1.5" />Copy result</Button>
      <Button type="button" size="sm" variant="outline" onClick={save}><Download className="h-4 w-4 mr-1.5" />Save as text</Button>
      <Button type="button" size="sm" variant="outline" onClick={print}><Printer className="h-4 w-4 mr-1.5" />Print</Button>
      {!rg.paid && (
        <p className="text-xs text-muted-foreground basis-full sm:basis-auto sm:ml-auto">
          Reading results never uses a trial use. Copying, saving or printing uses 1 per visit.
        </p>
      )}
    </div>
  );
}

export function ToolContainer({ children, className = "" }: ToolContainerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  return (
    <div ref={rootRef} className={`max-w-4xl mx-auto ${className}`}>
      {children}
      <ResultActionsBar rootRef={rootRef} />
    </div>
  );
}

interface ProcessingStateProps {
  message?: string;
  /** 0-100. When provided, shows a real, determinate progress bar
   * instead of just a spinner -- only pass this when the operation
   * genuinely tracks progress (e.g. page N of M), never a faked or
   * estimated percentage. Tools without real staged progress should
   * omit this and keep the honest, indeterminate spinner. */
  progress?: number;
  /** When provided, shows a Stop button. The tool decides what
   * "stopping" means for its own operation -- at minimum, resetting
   * back to the file-picker state and discarding the eventual result
   * when it arrives, even if the underlying computation can't be
   * truly interrupted mid-flight. */
  onStop?: () => void;
}

export function ProcessingState({ message = "Processing...", progress, onStop }: ProcessingStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
      <p className="text-sm text-muted-foreground">{message}</p>
      {typeof progress === "number" && (
        <div className="w-full max-w-xs mt-4">
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-200"
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground text-center mt-1">{Math.round(progress)}%</p>
        </div>
      )}
      {onStop && (
        <Button variant="outline" size="sm" className="mt-4" onClick={onStop}>
          Stop
        </Button>
      )}
    </div>
  );
}

interface DownloadButtonProps {
  /** Legacy custom handler. Prefer blob + fileName when the result is a file. */
  onClick?: () => void;
  label?: string;
  disabled?: boolean;
  /** When set with fileName, enables native Share (mobile) for the file. */
  blob?: Blob | null;
  fileName?: string;
  /**
   * For a button that runs its own onClick download (no blob yet): the file name it will save, so the trial-badge
   * notice shows BEFORE the click, exactly as it does when a blob is passed. Needed on every badged download path.
   */
  markHint?: string;
  /** Optional text share (for text-tool results). */
  shareText?: string;
  shareTitle?: string;
  /** Set false to hide Share (e.g. secondary format). Default true. */
  enableShare?: boolean;
  /** Optional overrides -- normally auto-derived from the current
   * tool's URL (see below), the same way FileDropZone already does.
   * Only pass these explicitly for the rare case where a page's own
   * route doesn't match the tool whose use is actually being logged. */
  toolId?: string;
  category?: string;
  /** A download that is not a result of the tool's work (the original handed back unchanged, say): costs no trial use. */
  freeDownload?: boolean;
  /** Replaces the default one-use-per-visit gate (e.g. logExportNow(key) for tools charged once per distinct export). Return false to stop. */
  gate?: () => Promise<boolean>;
  /** With `stacked`: a smaller second line under the label (the trial-use hint is appended to it). */
  subtitle?: string;
  /** Full-width, wrapping, 56px-tall button for phones (label over subtitle). Desktop keeps its natural width. */
  stacked?: boolean;
  /** On phones the primary Download button is pinned to the bottom (above the anchor ad) in a StickyActionBar, with this
   * summary text (size, file count...) beside it; the in-page copy is hidden below 640px. Desktop is unchanged. */
  pinned?: { summary?: React.ReactNode };
  /** Hide the "small badge" notice under the button below 640px, for a list of buttons where the tool shows it once above them. */
  hideMarkNoticeOnPhone?: boolean;
}

function canShareFiles(file: File): boolean {
  try {
    return typeof navigator !== "undefined"
      && typeof navigator.share === "function"
      && (typeof navigator.canShare !== "function" || navigator.canShare({ files: [file] }));
  } catch {
    return false;
  }
}

function canShareText(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

/**
 * Share a file via the Web Share API (mobile share sheet).
 * Returns true if share was invoked, false if unsupported.
 */
export async function shareBlob(
  blob: Blob,
  fileName: string,
  opts?: { title?: string; text?: string },
): Promise<boolean> {
  const marked = await markBlobForTrial(blob, fileName);
  const file = new File([marked], fileName, {
    type: marked.type || "application/octet-stream",
  });
  if (!canShareFiles(file)) return false;
  await navigator.share({
    files: [file],
    title: opts?.title || fileName,
    text: opts?.text || `Shared from FreeDailyPro`,
  });
  window.dispatchEvent(
    new CustomEvent("freedailypro:share-complete", { detail: { filename: fileName } }),
  );
  return true;
  // Was catching AbortError (user cancelled the share sheet) here and
  // returning false, the same signal as "sharing genuinely isn't
  // supported" -- that's what let DownloadButton's handleShare treat a
  // cancelled share exactly like an unsupported one, silently forcing a
  // download the person never asked for after they'd explicitly backed
  // out of sharing. Letting it propagate instead lets each caller
  // decide: DownloadButton now checks err.name === "AbortError"
  // specifically and does nothing for a real cancel; QrScanner's own
  // catch-all already treats any thrown error the same way (falls
  // through to copying the result), so this changes nothing there.
}

export async function shareText(
  text: string,
  opts?: { title?: string; url?: string },
): Promise<boolean> {
  if (!canShareText() || !text) return false;
  // Same reasoning as shareBlob above: a cancelled share now propagates
  // as a real AbortError instead of being silently folded into the same
  // `false` as "not supported", so a caller can tell the two apart.
  await navigator.share({
    title: opts?.title || "FreeDailyPro",
    text,
    url: opts?.url,
  });
  return true;
}

/**
 * Download and optional Share actions for tool results.
 * On mobile (and desktop browsers that support Web Share + files), Share
 * opens the system sheet so users can send the outcome without saving first.
 */
export function DownloadButton({
  onClick,
  label = "Download",
  disabled,
  blob,
  fileName,
  markHint,
  shareText: shareTextProp,
  shareTitle,
  enableShare = true,
  toolId: toolIdProp,
  category: categoryProp,
  freeDownload = false,
  gate,
  subtitle,
  stacked = false,
  pinned,
  hideMarkNoticeOnPhone = false,
}: DownloadButtonProps) {
  const { isAuthenticated } = useAuth();
  const { logUsageNow } = useLogUsageOnAction();
  const logUsage = trpc.usage.log.useMutation();
  const utils = trpc.useUtils();
  const params = useParams<{ id?: string }>();
  const pipeline = usePipelineContext();
  const [, navigate] = useLocation();
  useUsageSnapshot(); // re-render when the trial/paid state becomes known or changes

  // Auto-derive from the current URL, same as FileDropZone -- this is
  // what actually closed the gap described in the props comment above:
  // ~70 tools were using this component without ever passing toolId/
  // category manually, so logRealUse() below was silently a no-op for
  // all of them despite the logging code itself being correct.
  const toolId = toolIdProp ?? params.id ?? "unknown";
  const category = categoryProp ?? getToolById(toolId)?.category ?? "unknown";

  // On the last step of a bundle run the file is named after the person's own file plus the bundle.
  const onLastStep = !!pipeline && pipeline.currentStepIndex >= pipeline.steps.length - 1;
  const outName = fileName && pipeline && onLastStep ? pipeline.finalName(fileName) : fileName;
  const [advancing, setAdvancing] = useState(false);

  const file = useMemo(() => {
    if (!blob || !outName) return null;
    return new File([blob], outName, { type: blob.type || "application/octet-stream" });
  }, [blob, outName]);

  // Inside a bundle run, tell the run page what this step produced so it can show sizes.
  const recordOutput = pipeline?.recordOutput;
  const stepIndex = pipeline?.currentStepIndex ?? 0;
  useEffect(() => {
    if (recordOutput && blob && fileName) recordOutput(stepIndex, fileName, blob.size, blob);
  }, [recordOutput, stepIndex, blob, fileName]);

  const showFileShare = enableShare && file ? canShareFiles(file) : false;
  const showTextShare = enableShare && !showFileShare && !!shareTextProp && canShareText();

  // Same "log it, ignore failures, never block the real action" pattern
  // FileDropZone uses -- a download must never fail because telemetry did.
  // Guards on "unknown" now (not just falsy) since toolId/category always
  // resolve to *some* string after the auto-derive above -- "unknown"
  // specifically means this button rendered outside a real tool page
  // route, where logging would just pollute the data with a meaningless
  // entry rather than reflect a real tool's usage.
  const logRealUse = () => {
    if (toolId === "unknown" || category === "unknown") return;
    if (isToolSessionCounted(toolId)) return; // one tool session = one use
    markToolSessionCounted(toolId);
    try {
      const deviceId = isAuthenticated ? undefined : getOrCreateDeviceId();
      logUsage.mutate({ toolId, category, deviceId }, { onSuccess: () => utils.usage.check.invalidate() });
    } catch {
      /* ignore */
    }
  };

  /**
   * The moment a result leaves: counts the tool session's one use here if nothing earlier did, and when today's
   * trial uses are gone shows the pass dialog instead of letting the file go. Loading a file and running the
   * tool are free; only taking the result away needs a use (or a pass). A tool that passes its own toolId keeps
   * the older log-and-go behaviour, since the gate reads the tool from the page's route.
   */
  const allowResultOut = async (): Promise<boolean> => {
    if (freeDownload) return true;
    // A gate that fails or hangs ends in a message (or a save for a paid session), never in a dead button (B-0309).
    if (gate) return guardedGate(gate);
    if (toolIdProp) { logRealUse(); return true; }
    return logUsageNow("download");
  };

  const handleDownload = async () => {
    if (!(await allowResultOut())) return;
    if (blob && outName) {
      downloadBlob(blob, outName, { free: true });
      return;
    }
    onClick?.();
  };

  const handleShare = async () => {
    if (!(await allowResultOut())) return;
    try {
      if (blob && outName) {
        const ok = await shareBlob(blob, outName, { title: shareTitle || outName });
        if (!ok) downloadBlob(blob, outName, { free: true });
        return;
      }
      if (shareTextProp) {
        await shareText(shareTextProp, { title: shareTitle });
      }
    } catch (err) {
      // A real, cancelled share (user closed the native share sheet) is
      // not a failure -- respect it and do nothing, rather than forcing
      // a download they never asked for after explicitly backing out.
      if (err instanceof DOMException && err.name === "AbortError") return;
      // Confirmed as a real, reproducible bug, not assumed: clicking
      // Share here can throw NotAllowedError ("Permission denied") from
      // navigator.share itself -- a genuine browser/OS-level limitation
      // on file-sharing this app has no control over, not a bug in the
      // share call itself (canShare() had already said yes, and the
      // call was made correctly). The fallback to download is the right
      // behavior when share truly isn't available -- but doing it
      // silently is what actually produced the complaint: someone taps
      // Share and, with no explanation, gets a Download instead and
      // reasonably reads that as "the Share button is broken."
      console.error(err);
      if (blob && outName) {
        downloadBlob(blob, outName, { free: true });
        toast.info("Sharing isn't available here — downloaded the file instead.");
      }
    }
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${stacked ? "w-full" : ""}`}>
      {(() => {
        const noticeName = blob && fileName ? fileName : markHint;
        if (!noticeName || !wouldMark(blob && fileName ? blob.type : "", noticeName, toolId)) return null;
        return (
          <p className={`w-full order-last text-xs text-muted-foreground${hideMarkNoticeOnPhone ? " max-sm:hidden" : ""}`} data-testid="trial-mark-notice">
            {markKindFor(blob && fileName ? blob.type : "", noticeName) === "pdf"
              ? "This download will carry a small “Created with FreeDailyPro.com” badge in the bottom right corner of each page."
              : "This download will carry small “Created with FreeDailyPro.com” badges in the top and bottom right corners."}{" "}
            <button type="button" className="underline underline-offset-2 hover:text-foreground" onClick={() => navigate("/pricing")}>
              Go paid to avoid the mark
            </button>
            .
          </p>
        );
      })()}
      {stacked ? (
        <Button onClick={() => void handleDownload()} disabled={disabled} className="gap-3 h-auto min-h-14 w-full sm:w-auto whitespace-normal text-left py-2" data-testid="download-stacked">
          <Download className="h-4 w-4 shrink-0" />
          <span className="flex min-w-0 flex-col items-start">
            <span className="break-words">{label}</span>
            {(subtitle || !freeDownload) && (
              <span className="break-words text-xs font-normal opacity-90">
                {subtitle}
                {!freeDownload && <DownloadTrialHint toolId={toolIdProp} inline prefix={subtitle ? " · " : ""} />}
              </span>
            )}
          </span>
        </Button>
      ) : (
        <Button onClick={() => void handleDownload()} disabled={disabled} className={`gap-2 max-sm:min-h-11${pinned ? " max-sm:hidden" : ""}`}>
          <Download className="h-4 w-4" />
          {label}
        </Button>
      )}
      {pinned && (
        <StickyActionBar>
          <div className="min-w-0 flex-1 text-[13px] leading-tight text-muted-foreground" data-testid="pinned-download-summary">
            <span className="block break-words">{pinned.summary}</span>
            {!freeDownload && <DownloadTrialHint toolId={toolIdProp} />}
          </div>
          <Button onClick={() => void handleDownload()} disabled={disabled} className={`${STICKY_BAR_PRIMARY_CLASS} gap-2`} data-testid="pinned-download">
            <Download className="h-4 w-4 shrink-0" />
            {label}
          </Button>
        </StickyActionBar>
      )}
      <ExportUsageNote premiumOrZeroOnly />
      {!freeDownload && !stacked && <DownloadTrialHint toolId={toolIdProp} className={pinned ? "max-sm:hidden" : ""} />}
      {blob && outName && (
        <CloudPickerButton
          provider="google"
          mode="save"
          fileName={outName}
          disabled={disabled}
          beforeSave={allowResultOut}
          getBlob={async () => (wouldMark(blob.type, outName) || zipMayBeMarked(blob, outName) ? markBlobForTrial(blob, outName) : blob)}
        />
      )}
      {(showFileShare || showTextShare) && (
        <Button
          type="button"
          variant="secondary"
          onClick={() => void handleShare()}
          disabled={disabled}
          className="gap-2"
        >
          <Share2 className="h-4 w-4" />
          Share
        </Button>
      )}
      {!pipeline && blob && fileName && toolId !== "unknown" && continueWith(toolId).map((c) => (
        <Button
          key={c.toolId}
          type="button"
          variant="outline"
          onClick={() => { setHandoff({ blob, fileName, fromToolId: toolId, toToolId: c.toolId }); navigate(`/tool/${c.toolId}`); }}
          className="gap-2"
          data-testid={`continue-${c.toolId}`}
        >
          Continue with {c.name}
        </Button>
      ))}
      {pipeline && blob && fileName && pipeline.currentStepIndex < pipeline.steps.length - 1 && (
        <Button
          type="button"
          onClick={() => {
            if (advancing) return; // a second click while the next step loads must not skip a step
            setAdvancing(true);
            void allowResultOut().then((ok) => {
              if (!ok) { setAdvancing(false); return; }
              pipeline.provideStepOutput(blob, fileName);
            });
          }}
          disabled={disabled || advancing}
          className="gap-2"
          data-testid="continue-next-step"
        >
          {advancing ? "Loading the next step..." : `Continue to Step ${pipeline.currentStepIndex + 2}: ${pipeline.steps[pipeline.currentStepIndex + 1].tool.name}`}
        </Button>
      )}
    </div>
  );
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoking at once leaves an empty file on mobile Chromium, which starts reading the blob after click() returns.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  // Notify listeners (e.g. engagement prompts) that a download occurred.
  // Tools fire this via downloadBlob so engagement UI can react without
  // every tool file knowing about it.
  window.dispatchEvent(new CustomEvent("freedailypro:download-complete", { detail: { filename } }));
}

/**
 * Saves a file. For a trial session (not paid, not admin) a finished PDF or
 * image first gets the small "Made with FreeDailyPro.com" mark; see
 * lib/trialMark.ts for exactly what is and is not marked.
 */
export function downloadBlob(blob: Blob, filename: string, opts?: { free?: boolean }) {
  // `free`: a file that is not a result of the tool's work (the original handed back) costs no trial use.
  (opts?.free ? (save: () => void) => save() : withDownloadGate)(() => {
    if (wouldMark(blob.type, filename) || zipMayBeMarked(blob, filename)) {
      void markBlobForTrial(blob, filename).then(
        (b) => triggerDownload(b, filename),
        () => { toast.error("Couldn't prepare the file for download. Please try again."); },
      );
      return;
    }
    triggerDownload(blob, filename);
  });
}

/**
 * Shows what a tool actually produced, before the person commits to
 * downloading it -- the fix for "black box" tools where the only
 * feedback after processing is a download link with no way to check
 * the result actually looks right first.
 *
 * Deliberately handles each media type differently rather than one
 * generic behavior, since "preview" has a very different real cost
 * depending on what it actually is:
 * - Images: the browser's own decoder does this practically for free
 *   regardless of file size, so this always shows automatically.
 * - Audio/video: a native player with preload="metadata" doesn't
 *   actually buffer the whole file just by existing on the page, so
 *   this shows automatically below sizeWarnMB -- above it, becomes an
 *   opt-in "Load preview" button instead, since even initializing a
 *   large blob URL has some real cost worth letting the person choose.
 * - PDF: always opt-in via a button, regardless of file size -- unlike
 *   the others, rendering a PDF page is real, non-trivial CPU work
 *   (pdf.js decoding + canvas rasterization), and file size doesn't
 *   reliably predict page count or render cost the way it does for
 *   the other types. The pdf.js rendering path is also loaded via a
 *   dynamic import specifically so tools that never touch a PDF don't
 *   carry pdf.js in their own bundle.
 */
interface ResultPreviewProps {
  blob: Blob;
  /** MIME-type override, for blobs whose own .type isn't reliably set
   * (some construction paths leave it blank). */
  mimeType?: string;
  /** Above this size, audio/video previews become opt-in rather than
   * automatic. Ignored for images (always automatic) and PDFs (always
   * opt-in). Defaults to 25MB. */
  sizeWarnMB?: number;
}

export function ResultPreview({ blob, mimeType, sizeWarnMB = 25 }: ResultPreviewProps) {
  const type = mimeType || blob.type || "";
  const sizeMB = blob.size / (1024 * 1024);
  // The object URL is made and revoked inside one effect, so React's double-run of effects (Strict Mode)
  // cannot leave an element pointing at an already revoked blob: URL (ERR_FILE_NOT_FOUND).
  const [url, setUrl] = useState<string>("");
  useEffect(() => {
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);

  const [audioVideoLoaded, setAudioVideoLoaded] = useState(sizeMB <= sizeWarnMB);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [xlsxRows, setXlsxRows] = useState<string[][] | null>(null);
  const [xlsxLoading, setXlsxLoading] = useState(false);
  const [xlsxError, setXlsxError] = useState(false);
  const [pptxPreviewUrl, setPptxPreviewUrl] = useState<string | null>(null);
  const [pptxSlideCount, setPptxSlideCount] = useState<number | null>(null);
  const [pptxLoading, setPptxLoading] = useState(false);
  const [pptxError, setPptxError] = useState(false);

  const loadPdfPreview = async () => {
    setPdfLoading(true);
    setPdfError(false);
    try {
      const { loadPdfDocument, renderPageToJpegBlob } = await import("@/tools/pdf/utils");
      // A real File is needed here, not just the Blob -- wrap it with a
      // harmless placeholder name since loadPdfDocument only reads bytes.
      const asFile = new File([blob], "preview.pdf", { type: "application/pdf" });
      const pdf = await loadPdfDocument(asFile);
      const page = await pdf.getPage(1);
      const thumbBlob = await renderPageToJpegBlob(page, 0.8, 0.8);
      setPdfPreviewUrl(URL.createObjectURL(thumbBlob));
    } catch (err) {
      console.error("PDF preview failed:", err);
      setPdfError(true);
    } finally {
      setPdfLoading(false);
    }
  };

  const loadXlsxPreview = async () => {
    setXlsxLoading(true);
    setXlsxError(false);
    try {
      const XLSX = await import("xlsx");
      const buffer = await blob.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[firstSheetName];
      const allRows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false }) as unknown[][];
      // Capped to keep the preview table itself lightweight, regardless
      // of how large the actual spreadsheet is.
      const capped = allRows.slice(0, 10).map(row => row.slice(0, 8).map(cell => String(cell ?? "")));
      setXlsxRows(capped);
    } catch (err) {
      console.error("Excel preview failed:", err);
      setXlsxError(true);
    } finally {
      setXlsxLoading(false);
    }
  };

  const loadPptxPreview = async () => {
    setPptxLoading(true);
    setPptxError(false);
    try {
      const { getPptxPreview } = await import("@/tools/pdf/pptxRasterize");
      // A real File is needed here, not just the Blob -- getPptxPreview
      // only reads bytes, so the name/type here are harmless placeholders.
      const asFile = new File([blob], "preview.pptx", { type: blob.type });
      const preview = await getPptxPreview(asFile);
      setPptxPreviewUrl(preview.dataUrl);
      setPptxSlideCount(preview.slideCount);
    } catch (err) {
      console.error("PowerPoint preview failed:", err);
      setPptxError(true);
    } finally {
      setPptxLoading(false);
    }
  };

  if (type.startsWith("image/")) {
    return (
      <div className="rounded-lg border bg-muted/30 p-2">
        <img src={url || undefined} alt="Result preview" className="max-w-full max-h-96 mx-auto rounded object-contain" />
      </div>
    );
  }

  if (type.startsWith("audio/")) {
    if (!audioVideoLoaded) {
      return (
        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => setAudioVideoLoaded(true)}>
          <Eye className="h-4 w-4" /> Load preview ({sizeMB.toFixed(0)}MB)
        </Button>
      );
    }
    return <audio src={url || undefined} controls preload="metadata" className="w-full" />;
  }

  if (type.startsWith("video/")) {
    if (!audioVideoLoaded) {
      return (
        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => setAudioVideoLoaded(true)}>
          <Eye className="h-4 w-4" /> Load preview ({sizeMB.toFixed(0)}MB)
        </Button>
      );
    }
    return <video src={url || undefined} controls preload="metadata" className="max-w-full max-h-96 mx-auto rounded border" />;
  }

  if (type === "application/pdf") {
    if (pdfPreviewUrl) {
      return (
        <div className="rounded-lg border bg-muted/30 p-2">
          <img src={pdfPreviewUrl} alt="First page preview" className="max-w-full max-h-96 mx-auto rounded object-contain" />
          <p className="text-xs text-muted-foreground text-center mt-1">Page 1 preview</p>
        </div>
      );
    }
    return (
      <div className="space-y-1">
        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={loadPdfPreview} disabled={pdfLoading}>
          {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
          {pdfLoading ? "Rendering preview..." : "Preview first page"}
        </Button>
        {pdfError && <p className="text-xs text-destructive">Couldn't render a preview — the file itself is still fine to download.</p>}
      </div>
    );
  }

  if (type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
    if (xlsxRows) {
      return (
        <div className="rounded-lg border bg-muted/30 p-2 overflow-x-auto">
          <table className="text-xs w-full border-collapse">
            <tbody>
              {xlsxRows.map((row, i) => (
                <tr key={i} className={i === 0 ? "font-medium border-b" : "border-b border-border/50"}>
                  {row.map((cell, j) => (
                    <td key={j} className="px-2 py-1 whitespace-nowrap">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-muted-foreground text-center mt-1">First sheet preview, capped to 10 rows</p>
        </div>
      );
    }
    return (
      <div className="space-y-1">
        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={loadXlsxPreview} disabled={xlsxLoading}>
          {xlsxLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
          {xlsxLoading ? "Reading spreadsheet..." : "Preview data"}
        </Button>
        {xlsxError && <p className="text-xs text-destructive">Couldn't preview this spreadsheet — the file itself is still fine to download.</p>}
      </div>
    );
  }

  if (type === "application/vnd.openxmlformats-officedocument.presentationml.presentation") {
    if (pptxPreviewUrl) {
      return (
        <div className="rounded-lg border bg-muted/30 p-2">
          <img src={pptxPreviewUrl} alt="First slide preview" className="max-w-full max-h-96 mx-auto rounded object-contain" />
          <p className="text-xs text-muted-foreground text-center mt-1">
            Slide 1 preview{pptxSlideCount !== null && pptxSlideCount > 1 ? ` of ${pptxSlideCount} slides` : ""}
          </p>
        </div>
      );
    }
    return (
      <div className="space-y-1">
        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={loadPptxPreview} disabled={pptxLoading}>
          {pptxLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
          {pptxLoading ? "Rendering preview..." : "Preview first slide"}
        </Button>
        {pptxError && <p className="text-xs text-destructive">Couldn't render a preview — the file itself is still fine to use.</p>}
      </div>
    );
  }

  return null; // No meaningful visual preview for this type (e.g. plain text, zip, docx) -- not an error, just nothing to add here.
}
